import React, { useState, useEffect, useRef } from "react"
import {
  Box,
  AppBar,
  Toolbar,
  Typography,
  Drawer,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  CssBaseline,
  Badge,
  Button
} from "@mui/material"
import PeopleIcon from "@mui/icons-material/People"
import LocalHospitalIcon from "@mui/icons-material/LocalHospital"
import VideocamIcon from "@mui/icons-material/Videocam"
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings"
import LogoutIcon from "@mui/icons-material/Logout"
import NotificationsIcon from "@mui/icons-material/Notifications"
import IconButton from "@mui/material/IconButton"
import { supabase } from '../../Supabase/supabaseRealtimeClient';
import AddUserAdminDialog from "./AddUserAdminDialog"
import AddPartnerDialog from "./AddPartnerDialog"
import ClientList from "./ClientList"
import PartnerList from "./PartnerList"
import AdminUserList from "./AdminUserList"
import MedicoList from "./MedicoList"
import TelemedicinaTriagem from "../telemedicina/TelemedicinaTriagem"
import ChamadoList from "../chamado/ChamadoList"
import ClientDialog from "./ClientDialog"
import MenuIcon from "@mui/icons-material/Menu"
import useMediaQuery from "@mui/material/useMediaQuery"
import { useTheme } from "@mui/material/styles"
import logoImage from "../../assets/cropped_image.png"
import { Capacitor } from "@capacitor/core"
import { Preferences } from "@capacitor/preferences"
import { notificationService } from "../../services/NotificationService"

const drawerWidth = 240

type Cliente = {
  id: number
  user_id: string
  nome: string
  email: string
  telefone: string
  status: string
  tipo_plano: "basico" | "premium" | "saude24h"
  endereco?: any
}

const AdminPanel: React.FC = () => {
  const [selectedSection, setSelectedSection] = useState("Chamados")
  const [openDialog, setOpenDialog] = useState(false)
  const [dialogType, setDialogType] = useState("")
  const [hasNewChamado, setHasNewChamado] = useState(false)
  const [chamadosCount, setChamadosCount] = useState(0)
  const [telemedicinaCount, setTelemedicinaCount] = useState(0)
  const [editClient, setEditClient] = useState<Cliente | null>(null)
  const [mobileOpen, setMobileOpen] = useState(false)
  const [userInteracted, setUserInteracted] = useState(false)
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down("md"))
  const notificationIntervalRef = useRef<NodeJS.Timeout | null>(null)

  // Função para tocar a notificação
  const tocarNotificacao = async () => {

    try {
      // SEMPRE criar nova instância de áudio para garantir que funcione
      const audio = new Audio("/assets/notification.mp3")
      audio.volume = 0.7
      audio.preload = "auto"

      // Aguardar um pouco para o áudio carregar
      await new Promise(resolve => setTimeout(resolve, 50))

      const playPromise = audio.play()
      if (playPromise !== undefined) {
        await playPromise

        // Limpar o áudio após tocar
        audio.onended = () => {
          audio.remove?.()
        }

        return
      }
    } catch (error) {
    }

    // Fallback: Web Audio API (sempre criar novo contexto)
    try {
      const audioContext = new (window.AudioContext ||
        (window as any).webkitAudioContext)()

      if (audioContext.state === "suspended") {
        await audioContext.resume()
      }

      const oscillator = audioContext.createOscillator()
      const gainNode = audioContext.createGain()

      oscillator.connect(gainNode)
      gainNode.connect(audioContext.destination)

      oscillator.frequency.setValueAtTime(800, audioContext.currentTime)
      oscillator.type = "sine"
      gainNode.gain.setValueAtTime(0, audioContext.currentTime)
      gainNode.gain.linearRampToValueAtTime(
        0.3,
        audioContext.currentTime + 0.01
      )
      gainNode.gain.exponentialRampToValueAtTime(
        0.01,
        audioContext.currentTime + 0.4
      )

      oscillator.start(audioContext.currentTime)
      oscillator.stop(audioContext.currentTime + 0.4)

      // Fechar o contexto após usar
      setTimeout(() => {
        audioContext.close()
      }, 500)

    } catch (fallbackError) {

      // Último fallback: vibração
      if ("vibrate" in navigator) {
        navigator.vibrate([200, 100, 200])
      }
    }
  }

  // Função para iniciar o loop de notificação
  const iniciarLoopNotificacao = () => {
    // Limpar intervalo anterior se existir
    if (notificationIntervalRef.current) {
      clearInterval(notificationIntervalRef.current)
    }

    // Marcar que há uma notificação não visualizada
    localStorage.setItem("visualizado", "false")

    // Tocar a primeira notificação imediatamente
    tocarNotificacao()

    // Configurar intervalo para tocar a cada 3 segundos
    notificationIntervalRef.current = setInterval(() => {
      const visualizado = localStorage.getItem("visualizado")

      if (visualizado === "true") {
        // Parar o loop se foi visualizado
        if (notificationIntervalRef.current) {
          clearInterval(notificationIntervalRef.current)
          notificationIntervalRef.current = null
        }
        // Resetar também o estado visual
        setHasNewChamado(false)
      } else {
        // Continuar tocando se ainda não foi visualizado
        tocarNotificacao()
      }
    }, 3000) // 3 segundos
  }

  // Função para parar o loop de notificação
  const pararLoopNotificacao = () => {
    if (notificationIntervalRef.current) {
      clearInterval(notificationIntervalRef.current)
      notificationIntervalRef.current = null
    }
  }

  // Inicializar áudio corretamente
  useEffect(() => {

    // Detectar primeira interação (apenas para compatibilidade)
    const handleFirstInteraction = () => {
      setUserInteracted(true)
    }

    // Se ainda não interagiu, esperar interação
    if (!userInteracted) {
      document.addEventListener("click", handleFirstInteraction, {
        once: true,
        passive: true
      })
      document.addEventListener("touchstart", handleFirstInteraction, {
        once: true,
        passive: true
      })
      document.addEventListener("keydown", handleFirstInteraction, {
        once: true,
        passive: true
      })
    }

    // Cleanup ao desmontar o componente
    return () => {
      document.removeEventListener("click", handleFirstInteraction)
      document.removeEventListener("touchstart", handleFirstInteraction)
      document.removeEventListener("keydown", handleFirstInteraction)
      // Limpar intervalo ao desmontar o componente
      if (notificationIntervalRef.current) {
        clearInterval(notificationIntervalRef.current)
      }
    }
  }, [userInteracted])

  // ===== Função para buscar contadores de badges (Movida para fora do useEffect) =====
  const fetchCounts = React.useCallback(async () => {
    console.log('[AdminPanel] Buscando contadores de badges...');
    // Chamados (Suporte) - Verificando status reais do banco
    const { count: countChamados, error: errChamados } = await supabase
        .from('chamado')
        .select('*', { count: 'exact', head: true })
        .in('status', ['Pendente', 'pendente', 'aberto', 'novo', 'Em análise']); 
    
    if (errChamados) console.error('[AdminPanel] Erro ao buscar count chamados:', errChamados);
    if (countChamados !== null) {
        console.log('[AdminPanel] Chamados count:', countChamados);
        setChamadosCount(countChamados);
    }

    // Telemedicina (Triagem)
    const { count: countTelemed, error: errTelemed } = await supabase
        .from('video_atendimentos')
        .select('*', { count: 'exact', head: true })
        .eq('status', 'aguardando_triagem');
    
    if (errTelemed) console.error('[AdminPanel] Erro ao buscar count telemed:', errTelemed);
    if (countTelemed !== null) {
        console.log('[AdminPanel] Telemedicina count:', countTelemed);
        setTelemedicinaCount(countTelemed);
    }
  }, []);

  // ===== useEffect para monitorar contadores de badges =====
  useEffect(() => {
    // 1. Initial fetch
    fetchCounts();

    // 2. Subscribe to changes for Chamados
    const chamadosSub = supabase
        .channel('admin_badges_chamados')
        .on('postgres_changes', { event: '*', schema: 'public', table: 'chamado' }, (payload) => {
            console.log('[AdminPanel] Mudança detectada em chamados:', payload.eventType);
            fetchCounts();
        })
        .subscribe((status) => {
            console.log('[AdminPanel] Subscription status chamados:', status);
        });

    // 3. Subscribe to changes for Telemedicina
    const telemedSub = supabase
        .channel('admin_badges_telemed')
        .on('postgres_changes', { event: '*', schema: 'public', table: 'video_atendimentos' }, (payload: any) => {
            console.log('[AdminPanel] Mudança detectada em telemedicina:', payload.eventType);
            fetchCounts();
        })
        .subscribe((status) => {
            console.log('[AdminPanel] Subscription status telemed:', status);
        });

    return () => {
        supabase.removeChannel(chamadosSub);
        supabase.removeChannel(telemedSub);
    };
  }, [fetchCounts]);

  // ===== useEffect para escutar eventos globais =====
  useEffect(() => {
    // Inicializar o NotificationService global
    notificationService.startGlobalListening();

    const handleNovaNotificacao = () => {
      setHasNewChamado(true);
      fetchCounts(); // Forçar atualização do contador ao receber notificação
    };

    const handleDesativarNotificacao = () => {
      setHasNewChamado(false);
      pararLoopNotificacao();
    };

    window.addEventListener('novaNotificacao', handleNovaNotificacao);
    window.addEventListener('desativarNotificacao', handleDesativarNotificacao);

    return () => {
      // Parar o NotificationService ao desmontar o componente
      notificationService.stopGlobalListening();
      window.removeEventListener('novaNotificacao', handleNovaNotificacao);
      window.removeEventListener('desativarNotificacao', handleDesativarNotificacao);
    };
  }, []);

  // ===== useEffect para gerenciar notificação (CORRIGIDO) =====
  useEffect(() => {
    if (hasNewChamado) {
      iniciarLoopNotificacao()
    }
  }, [hasNewChamado])

  // ===== handleSectionChange =====
  const handleSectionChange = (section: string) => {
    setSelectedSection(section)
    // Não resetar mais automaticamente - só para quando visualizar
  }

  // ===== handleNotificationClick =====
  const handleNotificationClick = () => {
    // Parar o loop e marcar como visualizado
    localStorage.setItem("visualizado", "true")
    pararLoopNotificacao()
    setHasNewChamado(false)
    
    // Disparar evento global para sincronizar com outros componentes
    const event = new CustomEvent('desativarNotificacao');
    window.dispatchEvent(event);
  }

  // ===== handleOpenDialog =====
  const handleOpenDialog = (type: string) => {
    setDialogType(type)
    setOpenDialog(true)
  }

  // ===== handleCloseDialog =====
  const handleCloseDialog = () => {
    setOpenDialog(false)
    setEditClient(null)
  }

  // ===== handleLogout =====
  const handleLogout = async () => {
    try {
      // Parar loop ao fazer logout
      pararLoopNotificacao()

      await supabase.auth.signOut()
      localStorage.clear()
      if (Capacitor.isNativePlatform()) {
        try {
          await Preferences.clear()
        } catch (error) {
        }
      }
      window.location.replace("/") // ou use navigate('/', { replace: true }) se preferir React Router
    } catch (error) {
      localStorage.clear()
      if (Capacitor.isNativePlatform()) {
        try {
          await Preferences.clear()
        } catch (error) {
        }
      }
      window.location.replace("/")
    }
  }

  // ===== handleEditClient =====
  const handleEditClient = (cliente: Cliente) => {
    setEditClient(cliente)
    setDialogType("cliente")
    setOpenDialog(true)
  }

  // ===== handleSaveClient =====
  const handleSaveClient = (data: Cliente) => {
    handleCloseDialog()
  }

  // ===== renderDialogTitle =====
  const renderDialogTitle = () => {
    switch (dialogType) {
      case "cliente":
        return "Adicionar Novo Cliente"
      case "parceiro":
        return "Adicionar Novo Parceiro"
      case "usuario":
        return "Adicionar Novo Usuário Administrativo"
      default:
        return ""
    }
  }

  // ===== handleDrawerToggle =====
  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen)
  }

  // ===== renderMainContent =====
  const renderMainContent = () => {
    switch (selectedSection) {
      case "Chamados":
        return (
          <>
            <Box
              sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}
            >
              <Typography variant='h4'>Lista de Chamados</Typography>
            </Box>
            <ChamadoList />
          </>
        )
      case "Clientes":
        return (
          <>
            <Box
              sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}
            >
              <Typography variant='h4'>Gestão de Clientes</Typography>
              <Button
                variant='contained'
                color='primary'
                onClick={() => {
                  setEditClient(null) // Limpa o cliente em edição
                  setDialogType("cliente")
                  setOpenDialog(true)
                }}
              >
                Adicionar Cliente
              </Button>
            </Box>
            <ClientList onEditClient={handleEditClient} />
          </>
        )
      case "Socorristas":
        return (
          <>
            <Box
              sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}
            >
              <Typography variant='h4'>Gestão de Socorristas</Typography>
              <Button
                variant='contained'
                color='primary'
                onClick={() => handleOpenDialog("parceiro")}
              >
                Adicionar Socorrista
              </Button>
            </Box>
            <PartnerList />
          </>
        )
      case "Usuários Administrativos":
        return (
          <>
            <Box
              sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}
            >
              <Typography variant='h4'>
                Gestão de Usuários Administrativos
              </Typography>
              <Button
                variant='contained'
                color='primary'
                onClick={() => handleOpenDialog("usuario")}
              >
                Adicionar Usuário
              </Button>
            </Box>
            <AdminUserList />
          </>
        )
      case "Médicos":
        return (
          <>
             <Box
              sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}
            >
              <Typography variant='h4'>Gestão de Médicos</Typography>
              <Button
                variant='contained'
                color='primary'
                onClick={() => {
                  setEditClient(null)
                  setDialogType("medico")
                  setOpenDialog(true)
                }}
              >
                Adicionar Médico
              </Button>
            </Box>
            <MedicoList />
          </>
        )
      case "Telemedicina":
        return <TelemedicinaTriagem />
      default:
        return null
    }
  }

  return (
    <Box sx={{ display: "flex" }}>
      <CssBaseline />

      {/* Header */}
      <AppBar
        position='fixed'
        sx={{ zIndex: theme => theme.zIndex.drawer + 1 }}
      >
        <Toolbar sx={{ display: "flex", justifyContent: "space-between" }}>
          <Box sx={{ display: "flex", alignItems: "center" }}>
            {isMobile && (
              <IconButton
                color='inherit'
                aria-label='open drawer'
                edge='start'
                onClick={handleDrawerToggle}
                sx={{ mr: 2 }}
              >
                <MenuIcon />
              </IconButton>
            )}
            <Typography variant='h6' noWrap component='div'>
              Painel Administrativo
            </Typography>
          </Box>
          <IconButton
            color='inherit'
            sx={{ ml: 2 }}
            onClick={handleNotificationClick}
          >
            <Badge color='error' variant='dot' invisible={!hasNewChamado}>
              <NotificationsIcon />
            </Badge>
          </IconButton>
        </Toolbar>
      </AppBar>

      {/* Sidebar Drawer */}
      {/* Mobile Drawer */}
      {isMobile ? (
        <Drawer
          variant='temporary'
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: "block", md: "none" },
            "& .MuiDrawer-paper": {
              boxSizing: "border-box",
              width: drawerWidth
            }
          }}
        >
          {/* Drawer content */}
          <Box
            sx={{
              height: "100%",
              display: "flex",
              flexDirection: "column",
              justifyContent: "space-between"
            }}
          >
            <Box>
              <Toolbar />
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  width: "100%",
                  mt: 4,
                  mb: 1
                }}
              >
                <Box
                  component='img'
                  src={logoImage}
                  alt="Marca d'água Support Life"
                  sx={{
                    width: "80%",
                    pointerEvents: "none",
                    userSelect: "none",
                    display: "block"
                  }}
                />
              </Box>
              <Box sx={{ overflow: "auto" }}>
                <List>
                  <ListItemButton
                    selected={selectedSection === "Chamados"}
                    onClick={() => {
                      handleSectionChange("Chamados")
                      handleDrawerToggle()
                    }}
                  >
                    <ListItemIcon>
                      <AdminPanelSettingsIcon />
                    </ListItemIcon>
                    <ListItemText primary='Chamados' />
                    {chamadosCount > 0 && (
                        <Badge badgeContent={chamadosCount} color="error" sx={{ mr: 2 }} />
                    )}
                  </ListItemButton>
                  <ListItemButton
                    selected={selectedSection === "Clientes"}
                    onClick={() => {
                      handleSectionChange("Clientes")
                      handleDrawerToggle()
                    }}
                  >
                    <ListItemIcon>
                      <PeopleIcon />
                    </ListItemIcon>
                    <ListItemText primary='Clientes' />
                  </ListItemButton>
                  <ListItemButton
                    selected={selectedSection === "Socorristas"}
                    onClick={() => {
                      handleSectionChange("Socorristas")
                      handleDrawerToggle()
                    }}
                  >
                    <ListItemIcon>
                      <LocalHospitalIcon />
                    </ListItemIcon>
                    <ListItemText primary='Socorristas' />
                  </ListItemButton>
                  <ListItemButton
                    selected={selectedSection === "Médicos"}
                    onClick={() => {
                      handleSectionChange("Médicos")
                      handleDrawerToggle()
                    }}
                  >
                    <ListItemIcon>
                      <LocalHospitalIcon />
                    </ListItemIcon>
                    <ListItemText primary='Médicos' />
                  </ListItemButton>
                  <ListItemButton
                    selected={selectedSection === "Telemedicina"}
                    onClick={() => {
                      handleSectionChange("Telemedicina")
                      handleDrawerToggle()
                    }}
                  >
                    <ListItemIcon>
                      <VideocamIcon />
                    </ListItemIcon>
                    <ListItemText primary='Telemedicina (Triagem)' />
                    {telemedicinaCount > 0 && (
                        <Badge badgeContent={telemedicinaCount} color="error" sx={{ mr: 2 }} />
                    )}
                  </ListItemButton>
                  <ListItemButton
                    selected={selectedSection === "Usuários Administrativos"}
                    onClick={() => {
                      handleSectionChange("Usuários Administrativos")
                      handleDrawerToggle()
                    }}
                  >
                    <ListItemIcon>
                      <AdminPanelSettingsIcon />
                    </ListItemIcon>
                    <ListItemText primary='Usuários Administrativos' />
                  </ListItemButton>
                </List>
              </Box>
            </Box>
            <Box sx={{ p: 2 }}>
              <List>
                <ListItemButton
                  onClick={() => {
                    handleLogout()
                    handleDrawerToggle()
                  }}
                >
                  <ListItemIcon>
                    <LogoutIcon />
                  </ListItemIcon>
                  <ListItemText primary='Sair' />
                </ListItemButton>
              </List>
            </Box>
          </Box>
        </Drawer>
      ) : (
        <Drawer
          variant='permanent'
          sx={{
            width: drawerWidth,
            flexShrink: 0,
            display: { xs: "none", md: "flex" },
            [`& .MuiDrawer-paper`]: {
              width: drawerWidth,
              boxSizing: "border-box",
              display: "flex",
              flexDirection: "column",
              justifyContent: "space-between"
            }
          }}
          open
        >
          <Box>
            <Toolbar />
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                width: "100%",
                mt: 4,
                mb: 1
              }}
            >
              <Box
                component='img'
                src={logoImage}
                alt="Marca d'água Support Life"
                sx={{
                  width: "80%",
                  pointerEvents: "none",
                  userSelect: "none",
                  display: "block"
                }}
              />
            </Box>
            <Box sx={{ overflow: "auto" }}>
              <List>
                <ListItemButton
                  selected={selectedSection === "Chamados"}
                  onClick={() => handleSectionChange("Chamados")}
                >
                  <ListItemIcon>
                    <AdminPanelSettingsIcon />
                  </ListItemIcon>
                  <ListItemText primary='Chamados' />
                  {chamadosCount > 0 && (
                      <Badge badgeContent={chamadosCount} color="error" sx={{ mr: 2 }} />
                  )}
                </ListItemButton>
                <ListItemButton
                  selected={selectedSection === "Clientes"}
                  onClick={() => handleSectionChange("Clientes")}
                >
                  <ListItemIcon>
                    <PeopleIcon />
                  </ListItemIcon>
                  <ListItemText primary='Clientes' />
                </ListItemButton>
                <ListItemButton
                  selected={selectedSection === "Socorristas"}
                  onClick={() => handleSectionChange("Socorristas")}
                >
                  <ListItemIcon>
                    <LocalHospitalIcon />
                  </ListItemIcon>
                  <ListItemText primary='Socorristas' />
                </ListItemButton>
                <ListItemButton
                  selected={selectedSection === "Médicos"}
                  onClick={() => handleSectionChange("Médicos")}
                >
                  <ListItemIcon>
                    <LocalHospitalIcon />
                  </ListItemIcon>
                  <ListItemText primary='Médicos' />
                </ListItemButton>
                <ListItemButton
                  selected={selectedSection === "Telemedicina"}
                  onClick={() => handleSectionChange("Telemedicina")}
                >
                  <ListItemIcon>
                    <VideocamIcon />
                  </ListItemIcon>
                  <ListItemText primary='Telemedicina (Triagem)' />
                  {telemedicinaCount > 0 && (
                      <Badge badgeContent={telemedicinaCount} color="error" sx={{ mr: 2 }} />
                  )}
                </ListItemButton>
                <ListItemButton
                  selected={selectedSection === "Usuários Administrativos"}
                  onClick={() =>
                    handleSectionChange("Usuários Administrativos")
                  }
                >
                  <ListItemIcon>
                    <AdminPanelSettingsIcon />
                  </ListItemIcon>
                  <ListItemText primary='Usuários Administrativos' />
                </ListItemButton>
              </List>
            </Box>
          </Box>
          <Box sx={{ p: 2 }}>
            <List>
              <ListItemButton onClick={handleLogout}>
                <ListItemIcon>
                  <LogoutIcon />
                </ListItemIcon>
                <ListItemText primary='Sair' />
              </ListItemButton>
            </List>
          </Box>
        </Drawer>
      )}

      {/* Main Content */}
      <Box
        component='main'
        sx={{
          flexGrow: 1,
          p: { xs: 1, sm: 3 },
          width: { xs: "100%", md: `calc(100% - ${drawerWidth}px)` },
          minHeight: "100vh",
          boxSizing: "border-box"
        }}
      >
        {renderMainContent()}
      </Box>

      {/* Modal de Cliente */}
      {dialogType === "cliente" && (
        <ClientDialog
          open={openDialog}
          onClose={handleCloseDialog}
          onSave={handleSaveClient}
          initialData={editClient || undefined}
          isEditing={!!editClient}
        />
      )}

      {/* Diálogo de Formulário */}
      {dialogType === "parceiro" && (
        <AddPartnerDialog open={openDialog} onClose={handleCloseDialog} />
      )}
      {dialogType === "usuario" && (
        <AddUserAdminDialog open={openDialog} onClose={handleCloseDialog} />
      )}
    </Box>
  )
}

export default AdminPanel
